import React, { useState, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import Button from "components/ui/Button";
import Toggle from "components/ui/Toggle";
import SearchSelect from "components/ui/SearchSelect";
import GooglePlacesAutocomplete from "components/GooglePlacesAutoComplete";
import ServiceSelect from "components/contractorCompany/ServiceSelect";
import WorkWithSelect, {
  toWorkWithValue,
} from "components/contractorCompany/WorkWithSelect";
import { useGetContractorQuery } from "apis/contractor";
import { FiUpload, FiX } from "react-icons/fi";

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB
const MAX_WIDTH = 512;
const MAX_HEIGHT = 512;
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];

export default function ContractorCompanyEdit({ initialData = {}, onSubmit }) {
  const [logoPreview, setLogoPreview] = useState(initialData.logo || null);
  const [logoBase64, setLogoBase64] = useState(null);
  const [logoError, setLogoError] = useState("");
  const fileInputRef = useRef(null);

  const [addressValue, setAddressValue] = useState(initialData.address || "");

  const {
    register,
    handleSubmit,
    setValue,
    control,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: initialData.name || "",
      email: initialData.email || "",
      mobile: initialData.mobile || "",
      address: initialData.address || "",
      abn: initialData.abn || "",
      licenseNumber: initialData.licenseNumber || "",
      website: initialData.website || "",
      services: initialData.services || [],
      // Offices/agents the company works with — stored as two arrays, edited as
      // one exclusive value.
      workWith: toWorkWithValue(initialData),
      // Populated as { _id, name, email } on read, a raw id on write.
      owner: initialData.owner?._id || initialData.owner || null,
      ownerDoesJobs: !!initialData.ownerDoesJobs,
      calendarOptIn: !!initialData.calendarOptIn,
      bookingEmail: initialData.bookingEmail || "",
      turnAroundWeekends: !!initialData.turnAroundWeekends,
      status: initialData.status || "active",
    },
  });

  const calendarOptIn = watch("calendarOptIn");

  const { data: contractors = [] } = useGetContractorQuery({
    isDeleted: "false",
  });
  const ownerOptions = contractors.map((c) => ({
    value: c._id,
    label: c.email ? `${c.name || "Unnamed"} — ${c.email}` : c.name || c._id,
  }));

  const validateAndProcessImage = (file) => {
    return new Promise((resolve, reject) => {
      // Check file type
      if (!ACCEPTED_TYPES.includes(file.type)) {
        reject("Only JPG, PNG, and WebP files are allowed");
        return;
      }

      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        reject(
          `File must be less than 1MB (yours: ${(file.size / 1024 / 1024).toFixed(2)}MB)`
        );
        return;
      }

      // Check dimensions
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(objectUrl);

        if (img.width > MAX_WIDTH || img.height > MAX_HEIGHT) {
          reject(
            `Image must be ${MAX_WIDTH}×${MAX_HEIGHT}px or smaller (yours: ${img.width}×${img.height}px)`
          );
          return;
        }

        // Convert to base64
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => reject("Failed to read file");
        reader.readAsDataURL(file);
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject("Failed to load image");
      };

      img.src = objectUrl;
    });
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLogoError("");

    try {
      const base64 = await validateAndProcessImage(file);
      setLogoBase64(base64);
      setLogoPreview(base64);
    } catch (err) {
      setLogoError(typeof err === "string" ? err : "Invalid image");
      // Reset file input
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemoveLogo = () => {
    setLogoPreview(null);
    setLogoBase64(null);
    setLogoError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const submitForm = (data) => {
    const transformed = { ...data };

    // Strip empty strings
    Object.keys(transformed).forEach((key) => {
      if (transformed[key] === "") transformed[key] = undefined;
    });

    // Always keep name and status
    transformed.name = data.name;
    transformed.status = data.status;

    // Booleans and arrays must survive the empty-string strip above
    transformed.services = data.services || [];
    // Flattened back to the two stored arrays; `workWith` itself isn't a field
    transformed.offices = data.workWith?.offices || [];
    transformed.agents = data.workWith?.agents || [];
    delete transformed.workWith;
    // Always sent — null clears the owner, and the backend links whoever is
    // named here to this company.
    transformed.owner = data.owner || null;
    transformed.ownerDoesJobs = !!data.ownerDoesJobs;
    transformed.calendarOptIn = !!data.calendarOptIn;
    transformed.turnAroundWeekends = !!data.turnAroundWeekends;
    // Opted out → send "" so a stale booking email is cleared, not kept
    transformed.bookingEmail = data.calendarOptIn ? data.bookingEmail : "";

    // Attach logo
    if (logoBase64) {
      // New upload — send base64
      transformed.logo = logoBase64;
    } else if (logoPreview && logoPreview.startsWith("http")) {
      // Existing logo unchanged — send URL
      transformed.logo = logoPreview;
    } else {
      // Logo removed
      transformed.logo = undefined;
    }

    onSubmit(transformed);
  };

  return (
    <form onSubmit={handleSubmit(submitForm)} className="space-y-6">
      {/* Logo Upload */}
      <div className="space-y-2">
        <label className="form-label">Company Logo</label>
        <div className="flex items-start gap-4">
          {logoPreview ? (
            <div className="relative w-32 h-32 rounded-lg border border-mediumgray bg-white flex items-center justify-center p-2">
              <img
                src={logoPreview}
                alt="Logo preview"
                className="max-w-full max-h-full object-contain"
              />
              <button
                type="button"
                onClick={handleRemoveLogo}
                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600"
              >
                <FiX size={14} />
              </button>
            </div>
          ) : (
            <div
              onClick={() => fileInputRef.current?.click()}
              className="w-32 h-32 rounded-lg border-2 border-dashed border-mediumgray flex items-center justify-center cursor-pointer hover:border-darkgray transition-colors"
            >
              <FiUpload className="text-darkgray" size={24} />
            </div>
          )}
          <div className="flex-1">
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.webp"
              onChange={handleLogoChange}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="text-sm text-black underline hover:opacity-70"
            >
              {logoPreview ? "Change logo" : "Upload logo"}
            </button>
            <p className="text-xs text-darkgray mt-1">
              JPG, PNG, or WebP. Max 512×512px, max 1MB.
            </p>
            {logoError && (
              <p className="form-error-message mt-1">{logoError}</p>
            )}
          </div>
        </div>
      </div>

      {/* Company Name */}
      <div className="space-y-2">
        <label htmlFor="name" className="form-label">
          Company Name *
        </label>
        <input
          type="text"
          id="name"
          {...register("name", {
            required: "Company name is required",
            minLength: {
              value: 2,
              message: "Name must be at least 2 characters",
            },
            maxLength: {
              value: 100,
              message: "Name must be less than 100 characters",
            },
          })}
          className={`form-input border ${errors.name ? "border-red-500" : "border-mediumgray"}`}
          placeholder="e.g., ABC Photography"
        />
        {errors.name && (
          <p className="form-error-message">{errors.name.message}</p>
        )}
      </div>

      {/* Email & Mobile */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label htmlFor="email" className="form-label">
            Email *
          </label>
          <input
            type="email"
            id="email"
            {...register("email", {
              required: "Email is required",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Please enter a valid email address",
              },
            })}
            className={`form-input border ${errors.email ? "border-red-500" : "border-mediumgray"}`}
            placeholder="info@company.com"
          />
          {errors.email && (
            <p className="form-error-message">{errors.email.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="mobile" className="form-label">
            Mobile *
          </label>
          <input
            type="text"
            id="mobile"
            {...register("mobile", {
              required: "Mobile number is required",
              pattern: {
                value: /^[\d\s\+\-\(\)]{8,15}$/,
                message: "Please enter a valid phone number",
              },
            })}
            className={`form-input border ${errors.mobile ? "border-red-500" : "border-mediumgray"}`}
            placeholder="0400 000 000"
          />
          {errors.mobile && (
            <p className="form-error-message">{errors.mobile.message}</p>
          )}
        </div>
      </div>

      {/* Address */}
      <div className="space-y-2">
        <label className="form-label">Address</label>
        <GooglePlacesAutocomplete
          address={addressValue}
          setAddress={(val) => {
            setAddressValue(val);
            setValue("address", val);
          }}
          onSelectAddress={(shortAddress) => {
            setAddressValue(shortAddress);
            setValue("address", shortAddress);
          }}
          isNSWCheck={false}
          placeholder="Search for an address..."
        />
      </div>

      {/* ABN & License */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label htmlFor="abn" className="form-label">
            ABN
          </label>
          <input
            type="text"
            id="abn"
            {...register("abn", {
              pattern: {
                value: /^\d{11}$/,
                message: "ABN must be exactly 11 digits",
              },
            })}
            className={`form-input border ${errors.abn ? "border-red-500" : "border-mediumgray"}`}
            placeholder="XX XXX XXX XXX"
            maxLength={11}
          />
          {errors.abn && (
            <p className="form-error-message">{errors.abn.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="licenseNumber" className="form-label">
            License Number
          </label>
          <input
            type="text"
            id="licenseNumber"
            {...register("licenseNumber", {
              maxLength: { value: 50, message: "License number too long" },
            })}
            className={`form-input border ${errors.licenseNumber ? "border-red-500" : "border-mediumgray"}`}
            placeholder="License #"
          />
          {errors.licenseNumber && (
            <p className="form-error-message">{errors.licenseNumber.message}</p>
          )}
        </div>
      </div>

      {/* Website */}
      <div className="space-y-2">
        <label htmlFor="website" className="form-label">
          Website
        </label>
        <input
          type="text"
          id="website"
          {...register("website", {
            pattern: {
              value:
                /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/,
              message: "Please enter a valid URL",
            },
          })}
          className={`form-input border ${errors.website ? "border-red-500" : "border-mediumgray"}`}
          placeholder="https://www.company.com"
        />
        {errors.website && (
          <p className="form-error-message">{errors.website.message}</p>
        )}
      </div>

      {/* Services */}
      <div className="space-y-2">
        <label className="form-label">Services</label>
        <Controller
          name="services"
          control={control}
          render={({ field, fieldState }) => (
            <ServiceSelect
              value={field.value}
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />
      </div>

      {/* Who they work with — offices or individual agents. Same picker the
          contractor portal shows on onboarding. Optional here: admins often
          create the company before the coverage is decided. */}
      <div className="space-y-2">
        <label className="form-label">Works with</label>
        <p className="text-xs text-darkgray">
          Select offices or individual agents. Leave empty if not decided yet.
        </p>
        <Controller
          name="workWith"
          control={control}
          render={({ field, fieldState }) => (
            <WorkWithSelect
              value={field.value || { offices: [], agents: [] }}
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />
      </div>

      {/* Calendar invite opt-in + booking email. The email input reads the
          watched opt-in value so the two can never disagree. */}
      <div className="space-y-2">
        <label className="flex items-start gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            {...register("calendarOptIn", {
              onChange: (e) => {
                if (!e.target.checked) setValue("bookingEmail", "");
              },
            })}
            className="mt-1"
          />
          <span className="text-sm text-darkgray">
            Opt in for all calendar invites for this company
          </span>
        </label>

        {calendarOptIn && (
          <div className="mt-3 space-y-2">
            <label htmlFor="bookingEmail" className="form-label">
              Booking email
            </label>
            <input
              type="email"
              id="bookingEmail"
              {...register("bookingEmail", {
                validate: (v, formValues) => {
                  if (!formValues.calendarOptIn) return true;
                  if (!v) return "Booking email is required when opted in";
                  return (
                    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ||
                    "Enter a valid email"
                  );
                },
              })}
              className={`form-input border ${
                errors.bookingEmail ? "border-red-500" : "border-mediumgray"
              }`}
              placeholder="bookings@example.com"
            />
            {errors.bookingEmail && (
              <p className="form-error-message">
                {errors.bookingEmail.message}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Owner */}
      <div className="space-y-2">
        <label className="form-label">Owner</label>
        <Controller
          name="owner"
          control={control}
          render={({ field }) => (
            <SearchSelect
              value={ownerOptions.find((o) => o.value === field.value) || null}
              onChange={(opt) => field.onChange(opt?.value || null)}
              options={ownerOptions}
              placeholder="Select owner..."
              isClearable
            />
          )}
        />
        <p className="text-xs text-darkgray">
          The contractor who runs this company. Selecting them also moves them
          into this company.
        </p>
      </div>

      {/* Owner-operator flag */}
      <label className="flex items-start gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          {...register("ownerDoesJobs")}
          className="mt-1"
        />
        <span>
          <span className="block text-sm font-medium text-black">
            Owner also does jobs themselves
          </span>
          <span className="block text-sm text-darkgray">
            The owner works jobs as well as running the company.
          </span>
        </span>
      </label>

      {/* Turnaround weekends — company-wide */}
      <div className="flex items-center justify-between gap-4 bg-white rounded-lg border border-mediumgray p-4">
        <div>
          <p className="text-sm font-semibold text-black">Includes weekends</p>
          <p className="text-sm text-darkgray mt-0.5">
            Turnaround times include Saturday and Sunday.
          </p>
        </div>
        <Controller
          name="turnAroundWeekends"
          control={control}
          render={({ field }) => (
            <Toggle checked={!!field.value} onChange={field.onChange} />
          )}
        />
      </div>

      {/* Status */}
      <div className="space-y-2">
        <label htmlFor="status" className="form-label">
          Status
        </label>
        <select
          id="status"
          {...register("status")}
          className="form-input border border-mediumgray"
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Submit */}
      <div className="flex justify-end mt-6 pt-4 border-t border-mediumgray">
        <Button type="submit" className="black-button">
          {initialData._id ? "Update Company" : "Create Company"}
        </Button>
      </div>
    </form>
  );
}
